import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {useSelector} from 'react-redux'
import {http} from 'hiya-common/src/utils/axios'
import {
  MiddleContestAuditionToRankRequestParams,
  MiddleContestAuditionToRankResponse,
  MiddleContestRankRequestParams,
  MiddleContestRankResponse,
  MiddleContestRewardListRequestParams,
  MiddleContestRewardListResponse,
  MiddleContestCardInfoResponse,
  MiddleContestCardRequestParams,
  MiddleContestHomepageRequestParams,
  MiddleContestHomepageResponse,
  MiddleContestPersonListRequestParams,
  MiddleContestPersonListResponse,
  MiddleContestRepechageResponse,
  MiddleContestTaskRecordRequestParams,
  MiddleContestTaskRecordResponse,
  MiddleContestTaskListResponse,
  MiddleContestTaskState,
  MiddleContestRemindUseCardRequestParams,
  MiddleContestRemindUseCardResponse,
  MiddleContestUnionActionRequestParams,
  MiddleContestUnionActionResponse,
  MiddleContestUnionLotteryRequestParams,
  MiddleContestUnionLotteryResponse,
  NewYearUnionGame2026State,
} from './types'
import {
  DEFAULT_CARD_PAGE_LIMIT,
  emptyAuditionToRankData,
  emptyRankData,
  emptyRewardListData,
  normalizeAuditionToRankResponse,
  normalizeHomepageResponse,
  normalizeRankResponse,
  normalizeRewardListResponse,
  normalizeRepechageResponse,
  normalizeTaskListResponse,
  normalizeTaskRecordResponse,
  normalizePersonListResponse,
  pickCardInfoScalars,
} from './utils'
import {RootState} from '~/stores'

const emptyHomepageData: NewYearUnionGame2026State['homepage']['data'] =
  normalizeHomepageResponse({})

/**
 * 2026 年中公会赛 - 活动首页
 * POST /live/activity/get_2026_year_middle_contest_homepage
 */
export const get2026YearMiddleUnionContestHomepage = createAsyncThunk<
  MiddleContestHomepageResponse,
  MiddleContestHomepageRequestParams | undefined,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleUnionContestHomepage',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_homepage',
      {
        ...(params?.from ? {from: params.from} : {}),
        ...(params?.match != null ? {match: params.match} : {}),
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestHomepageResponse
    }
    return data as MiddleContestHomepageResponse
  }
)

/**
 * 2026 年公会赛 - 奖励列表
 * POST /live/activity/get_2026_year_middle_contest_reward_list
 */
export const get2026YearMiddleContestRewardList = createAsyncThunk<
  MiddleContestRewardListResponse,
  MiddleContestRewardListRequestParams | undefined,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleContestRewardList',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_reward_list',
      {
        ...(params?.from ? {from: params.from} : {}),
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestRewardListResponse
    }
    return (data ?? {}) as MiddleContestRewardListResponse
  }
)

const emptyCardData: NewYearUnionGame2026State['card']['data'] = {
  is_buff_active: false,
  competition_phase: 0,
  final_contest_start_time: '',
  current_ratio: 0,
  buff_expire_ts: 0,
  user_identity: 0,
  is_remind_can_use: false,
  card_list: [],
  new_offset: 0,
  more: false,
  err_msg: '',
  count: 0,
}

/**
 * 2026 年中公会赛 - 加成卡状态及历史列表（分页）
 * POST /live/activity/get_2026_year_middle_contest_card_info
 */
export const get2026YearMiddleContestCardInfo = createAsyncThunk<
  MiddleContestCardInfoResponse,
  MiddleContestCardRequestParams | undefined,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleContestCardInfo',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_card_info',
      {
        offset: params?.offset ?? 0,
        limit: params?.limit ?? DEFAULT_CARD_PAGE_LIMIT,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestCardInfoResponse
    }
    return data as MiddleContestCardInfoResponse
  }
)

const emptyPersonData: NewYearUnionGame2026State['person']['data'] = {
  from: '',
  err_msg: '',
  income_threshold: 0,
  can_one_click_call: false,
  card_mul_min: 1.4,
  card_mul_max: 1.5,
  list: [],
}

/**
 * 2026 年中公会赛 - 号召人员列表
 * POST /live/activity/get_2026_year_middle_union_contest_person_list
 */
export const get2026YearMiddleContestPersonList = createAsyncThunk<
  MiddleContestPersonListResponse,
  MiddleContestPersonListRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleContestPersonList',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_union_contest_person_list',
      {
        from: params.from,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestPersonListResponse
    }
    return data as MiddleContestPersonListResponse
  }
)

const emptyRepechageData: NewYearUnionGame2026State['repechage']['data'] =
  normalizeRepechageResponse({})

/**
 * 2026 年中公会赛 - 抢位赛（无请求参数）
 * POST /live/activity/get_2026_year_middle_contest_repechage
 */
export const get2026YearMiddleUnionContestRepechage = createAsyncThunk<
  MiddleContestRepechageResponse,
  void | undefined,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleUnionContestRepechage',
  async (_params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_repechage',
      {},
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestRepechageResponse
    }
    return data as MiddleContestRepechageResponse
  }
)

const emptyTaskRecordData: NewYearUnionGame2026State['taskRecord']['data'] = {
  from: '',
  ...normalizeTaskRecordResponse({}),
}

const emptyTaskData: Pick<
  MiddleContestTaskState,
  'groupName' | 'list' | 'activity_id' | 'lottery_info'
> = normalizeTaskListResponse({})

/**
 * 2026 年中公会赛 - 闯关任务列表
 * POST /live/activity/get_2026_year_union_middle_contest_task_list
 */
export const get2026YearUnionMiddleContestTaskList = createAsyncThunk<
  MiddleContestTaskListResponse,
  Record<string, unknown> | undefined,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearUnionMiddleContestTaskList',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_union_middle_contest_task_list',
      {...params},
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestTaskListResponse
    }
    return data as MiddleContestTaskListResponse
  }
)

/**
 * 2026 年中公会赛 - 子榜（海选/晋级/海选直通车）
 * POST /live/activity/get_2026_year_middle_contest_to_rank
 */
export const get2026YearMiddleUnionContestAuditionToRank = createAsyncThunk<
  MiddleContestAuditionToRankResponse,
  MiddleContestAuditionToRankRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleUnionContestAuditionToRank',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_to_rank',
      {
        union_id: params.union_id,
        from: params.from,
        date: params.date,
        ...(params.offset != null ? {offset: params.offset} : {}),
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestAuditionToRankResponse
    }
    return data as MiddleContestAuditionToRankResponse
  }
)

/**
 * 2026 年中公会赛 - 父榜单（海选/晋级/海选直通车）
 * POST /live/activity/get_2026_year_middle_contest_rank
 */
export const get2026YearMiddleUnionContestRank = createAsyncThunk<
  MiddleContestRankResponse,
  MiddleContestRankRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleUnionContestRank',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_contest_rank',
      {
        from: params.from,
        ...(params.tab != null ? {tab: params.tab} : {}),
        ...(params.date ? {date: params.date} : {}),
        ...(params.offset != null ? {offset: params.offset} : {}),
        ...(params.limit != null ? {limit: params.limit} : {}),
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestRankResponse
    }
    return data as MiddleContestRankResponse
  }
)

/**
 * 2026 年中公会赛 - 工会动作（点赞/提醒/开始任务/搜索等）
 * POST /live/activity/get_2026_year_middle_union_contest_action
 */
export const post2026YearMiddleUnionContestAction = createAsyncThunk<
  MiddleContestUnionActionResponse,
  MiddleContestUnionActionRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/post2026YearMiddleUnionContestAction',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_union_contest_action',
      {...params},
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestUnionActionResponse
    }
    return (data ?? {err_msg: ''}) as MiddleContestUnionActionResponse
  }
)

/**
 * 主播提醒公会长使用未使用的加成卡
 * POST /live/activity/get_2026_year_middle_union_contest_use_card
 */
export const post2026YearMiddleUnionContestUseCard = createAsyncThunk<
  MiddleContestRemindUseCardResponse,
  MiddleContestRemindUseCardRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/post2026YearMiddleUnionContestUseCard',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_union_contest_use_card',
      {card_id: params.card_id},
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestRemindUseCardResponse
    }
    return (data ?? {err_msg: ''}) as MiddleContestRemindUseCardResponse
  }
)

/**
 * 2026 年中公会赛 - 抽奖
 * POST /live/commonactivity/lottery
 */
export const post2026YearMiddleUnionContestLottery = createAsyncThunk<
  MiddleContestUnionLotteryResponse,
  MiddleContestUnionLotteryRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/post2026YearMiddleUnionContestLottery',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/commonactivity/lottery',
      {
        lottery_id: params.lottery_id,
        times: params.times ?? 1,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestUnionLotteryResponse
    }
    return (data ?? {err_msg: ''}) as MiddleContestUnionLotteryResponse
  }
)

/** @deprecated 使用 post2026YearMiddleUnionContestLottery */
export const post2026YearMiddleUnionContestLotteryWrap =
  post2026YearMiddleUnionContestLottery

/**
 * 2026 年中公会赛 - 闯关/宝箱记录
 * POST /live/activity/get_2026_year_middle_union_contest_task_record
 */
export const get2026YearMiddleUnionContestTaskRecord = createAsyncThunk<
  MiddleContestTaskRecordResponse,
  MiddleContestTaskRecordRequestParams,
  {state: RootState}
>(
  'NewYearUnionGame2026/get2026YearMiddleUnionContestTaskRecord',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2026_year_middle_union_contest_task_record',
      {from: params.from},
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as MiddleContestTaskRecordResponse
    }
    return data as MiddleContestTaskRecordResponse
  }
)

const emptyAuditionToRankDataState: NewYearUnionGame2026State['auditionToRank']['data'] =
  emptyAuditionToRankData()

const emptyRankDataState: NewYearUnionGame2026State['rank']['data'] =
  emptyRankData()

const emptyRewardListDataState: NewYearUnionGame2026State['rewardList']['data'] =
  emptyRewardListData()

const emptyActionState: NewYearUnionGame2026State['action'] = {
  loading: false,
  error: null,
  lastFrom: '',
  lastResult: null,
}

const emptyRemindUseCardState: NewYearUnionGame2026State['remindUseCard'] = {
  loading: false,
  error: null,
  lastCardId: 0,
  lastResult: null,
}

const emptyLotteryState: NewYearUnionGame2026State['lottery'] = {
  loading: false,
  error: null,
  lastResult: null,
}

const initialState: NewYearUnionGame2026State = {
  homepage: {
    loading: false,
    error: null,
    data: {...emptyHomepageData},
  },
  action: {...emptyActionState},
  remindUseCard: {...emptyRemindUseCardState},
  lottery: {...emptyLotteryState},
  auditionToRank: {
    loading: false,
    error: null,
    data: {...emptyAuditionToRankDataState},
  },
  rank: {
    loading: false,
    error: null,
    data: {...emptyRankDataState},
  },
  rewardList: {
    loading: false,
    error: null,
    data: {...emptyRewardListDataState},
  },
  card: {
    loading: false,
    error: null,
    data: {...emptyCardData},
  },
  person: {
    loading: false,
    error: null,
    data: {...emptyPersonData},
  },
  repechage: {
    loading: false,
    error: null,
    data: {...emptyRepechageData},
  },
  taskRecord: {
    loading: false,
    error: null,
    data: {...emptyTaskRecordData},
  },
  task: {
    loading: false,
    error: null,
    done: false,
    ...emptyTaskData,
  },
}

export const NewYearUnionGame2026Slice = createSlice({
  name: 'NewYearUnionGame2026',
  initialState,
  reducers: {
    resetCardList: (state) => {
      state.card.data.card_list = []
      state.card.data.new_offset = 0
      state.card.data.more = false
    },
    resetPersonList: (state) => {
      state.person.data = {...emptyPersonData}
    },
    resetRepechage: (state) => {
      state.repechage.data = {...emptyRepechageData}
    },
    resetTaskRecord: (state) => {
      state.taskRecord.data = {...emptyTaskRecordData}
    },
    resetTask: (state) => {
      Object.assign(state.task, {
        loading: false,
        error: null,
        done: false,
        ...emptyTaskData,
      })
    },
    resetHomepage: (state) => {
      state.homepage.data = {...emptyHomepageData}
    },
    resetLottery: (state) => {
      Object.assign(state.lottery, emptyLotteryState)
    },
    resetAction: (state) => {
      Object.assign(state.action, emptyActionState)
    },
    resetRemindUseCard: (state) => {
      Object.assign(state.remindUseCard, emptyRemindUseCardState)
    },
    resetAuditionToRank: (state) => {
      state.auditionToRank.data = {...emptyAuditionToRankDataState}
    },
    resetRank: (state) => {
      state.rank.data = {...emptyRankDataState}
    },
    resetRewardList: (state) => {
      state.rewardList.data = {...emptyRewardListDataState}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(get2026YearMiddleContestRewardList.pending, (state) => {
        state.rewardList.loading = true
        state.rewardList.error = null
      })
      .addCase(get2026YearMiddleContestRewardList.fulfilled, (state, action) => {
        state.rewardList.loading = false
        const payload =
          action.payload ??
          ({} as MiddleContestRewardListResponse & Record<string, unknown>)
        state.rewardList.data = normalizeRewardListResponse(
          payload as MiddleContestRewardListResponse & Record<string, unknown>,
          action.meta.arg?.from ?? ''
        )
      })
      .addCase(get2026YearMiddleContestRewardList.rejected, (state, action) => {
        state.rewardList.loading = false
        state.rewardList.error = action.error.message || '获取奖励列表失败'
      })
      .addCase(get2026YearMiddleUnionContestRank.pending, (state) => {
        state.rank.loading = true
        state.rank.error = null
      })
      .addCase(get2026YearMiddleUnionContestRank.fulfilled, (state, action) => {
        state.rank.loading = false
        const payload = action.payload ?? ({} as MiddleContestRankResponse)
        const args = action.meta.arg
        const normalized = normalizeRankResponse(payload, {
          from: args.from,
          tab: args.tab,
          date: args.date,
        })
        const shouldAppend = args.append && (args.offset ?? 0) > 0

        if (shouldAppend) {
          state.rank.data = {
            ...normalized,
            list: [...state.rank.data.list, ...normalized.list],
            country_list:
              normalized.country_list ?? state.rank.data.country_list,
          }
        } else {
          const prev = state.rank.data
          state.rank.data = {
            ...normalized,
            list: normalized.list.length > 0 ? normalized.list : prev.list,
            own: normalized.own ?? prev.own,
            group_list: normalized.group_list.length
              ? normalized.group_list
              : prev.group_list,
            country_list: normalized.country_list ?? prev.country_list,
            reward_need_score:
              normalized.reward_need_score > 0
                ? normalized.reward_need_score
                : prev.reward_need_score,
          }
        }
      })
      .addCase(get2026YearMiddleUnionContestRank.rejected, (state, action) => {
        state.rank.loading = false
        state.rank.error = action.error.message || '获取榜单失败'
      })
      .addCase(get2026YearMiddleUnionContestAuditionToRank.pending, (state) => {
        state.auditionToRank.loading = true
        state.auditionToRank.error = null
      })
      .addCase(get2026YearMiddleUnionContestAuditionToRank.fulfilled, (state, action) => {
        state.auditionToRank.loading = false
        const payload = action.payload ?? ({} as MiddleContestAuditionToRankResponse)
        const args = action.meta.arg
        const normalized = normalizeAuditionToRankResponse(payload, {
          union_id: args.union_id,
          from: args.from,
          date: args.date,
        })
        const shouldAppend =
          args.append && (args.offset ?? 0) > 0

        if (shouldAppend) {
          state.auditionToRank.data = {
            ...normalized,
            support_anchors: [
              ...state.auditionToRank.data.support_anchors,
              ...normalized.support_anchors,
            ],
          }
        } else {
          const prev = state.auditionToRank.data
          state.auditionToRank.data = {
            ...normalized,
            support_anchors:
              normalized.support_anchors.length > 0
                ? normalized.support_anchors
                : prev.support_anchors,
            own: normalized.own ?? prev.own,
          }
        }
      })
      .addCase(get2026YearMiddleUnionContestAuditionToRank.rejected, (state, action) => {
        state.auditionToRank.loading = false
        state.auditionToRank.error = action.error.message || '获取子榜失败'
      })
      .addCase(post2026YearMiddleUnionContestAction.pending, (state) => {
        state.action.loading = true
        state.action.error = null
      })
      .addCase(post2026YearMiddleUnionContestAction.fulfilled, (state, action) => {
        state.action.loading = false
        state.action.lastFrom = action.meta.arg.from
        state.action.lastResult = action.payload ?? {err_msg: ''}
      })
      .addCase(post2026YearMiddleUnionContestAction.rejected, (state, action) => {
        state.action.loading = false
        state.action.error = action.error.message || '操作失败'
      })
      .addCase(post2026YearMiddleUnionContestUseCard.pending, (state) => {
        state.remindUseCard.loading = true
        state.remindUseCard.error = null
      })
      .addCase(post2026YearMiddleUnionContestUseCard.fulfilled, (state, action) => {
        state.remindUseCard.loading = false
        state.remindUseCard.lastCardId = action.meta.arg.card_id
        state.remindUseCard.lastResult = action.payload ?? {err_msg: ''}
      })
      .addCase(post2026YearMiddleUnionContestUseCard.rejected, (state, action) => {
        state.remindUseCard.loading = false
        state.remindUseCard.error = action.error.message || '提醒使用加成卡失败'
      })
      .addCase(post2026YearMiddleUnionContestLottery.pending, (state) => {
        state.lottery.loading = true
        state.lottery.error = null
      })
      .addCase(post2026YearMiddleUnionContestLottery.fulfilled, (state, action) => {
        state.lottery.loading = false
        state.lottery.lastResult = action.payload ?? {err_msg: ''}
        const ticketNum = action.payload?.ticket_num
        const lotteryId = action.meta.arg.lottery_id
        if (ticketNum != null && lotteryId > 0 && state.task.lottery_info.length) {
          const target =
            state.task.lottery_info.find(
              (item) => item.lottery_id === lotteryId
            ) ?? state.task.lottery_info[0]
          if (target) {
            target.ticket_num = ticketNum
          }
        }
      })
      .addCase(post2026YearMiddleUnionContestLottery.rejected, (state, action) => {
        state.lottery.loading = false
        state.lottery.error = action.error.message || '抽奖失败'
      })
      .addCase(get2026YearMiddleUnionContestHomepage.pending, (state) => {
        state.homepage.loading = true
        state.homepage.error = null
      })
      .addCase(get2026YearMiddleUnionContestHomepage.fulfilled, (state, action) => {
        state.homepage.loading = false
        const payload = action.payload ?? ({} as MiddleContestHomepageResponse)
        state.homepage.data = normalizeHomepageResponse(payload)
      })
      .addCase(get2026YearMiddleUnionContestHomepage.rejected, (state, action) => {
        state.homepage.loading = false
        state.homepage.error = action.error.message || '获取活动首页失败'
      })
      .addCase(get2026YearMiddleContestCardInfo.pending, (state) => {
        state.card.loading = true
        state.card.error = null
      })
      .addCase(get2026YearMiddleContestCardInfo.fulfilled, (state, action) => {
        state.card.loading = false
        const payload = action.payload ?? ({} as MiddleContestCardInfoResponse)
        const scalars = pickCardInfoScalars(payload)
        const nextList = payload.card_list ?? []
        const shouldAppend =
          action.meta.arg?.append && (action.meta.arg?.offset ?? 0) > 0

        if (shouldAppend) {
          state.card.data = {
            ...state.card.data,
            ...scalars,
            card_list: [...state.card.data.card_list, ...nextList],
          }
        } else {
          const prev = state.card.data
          state.card.data = {
            ...prev,
            ...scalars,
            card_list: nextList.length > 0 ? nextList : prev.card_list,
          }
        }
      })
      .addCase(get2026YearMiddleContestCardInfo.rejected, (state, action) => {
        state.card.loading = false
        state.card.error = action.error.message || '获取加成卡信息失败'
      })
      .addCase(get2026YearMiddleContestPersonList.pending, (state) => {
        state.person.loading = true
        state.person.error = null
      })
      .addCase(get2026YearMiddleContestPersonList.fulfilled, (state, action) => {
        state.person.loading = false
        const payload = action.payload ?? ({} as MiddleContestPersonListResponse)
        state.person.data = {
          from: action.meta.arg.from,
          ...normalizePersonListResponse(payload),
        }
      })
      .addCase(get2026YearMiddleContestPersonList.rejected, (state, action) => {
        state.person.loading = false
        state.person.error = action.error.message || '获取号召人员列表失败'
      })
      .addCase(get2026YearMiddleUnionContestRepechage.pending, (state) => {
        state.repechage.loading = true
        state.repechage.error = null
      })
      .addCase(get2026YearMiddleUnionContestRepechage.fulfilled, (state, action) => {
        state.repechage.loading = false
        const payload = action.payload ?? ({} as MiddleContestRepechageResponse)
        state.repechage.data = normalizeRepechageResponse(payload)
      })
      .addCase(get2026YearMiddleUnionContestRepechage.rejected, (state, action) => {
        state.repechage.loading = false
        state.repechage.error = action.error.message || '获取抢位赛信息失败'
      })
      .addCase(get2026YearMiddleUnionContestTaskRecord.pending, (state) => {
        state.taskRecord.loading = true
        state.taskRecord.error = null
      })
      .addCase(get2026YearMiddleUnionContestTaskRecord.fulfilled, (state, action) => {
        state.taskRecord.loading = false
        const payload = action.payload ?? ({} as MiddleContestTaskRecordResponse)
        state.taskRecord.data = {
          from: action.meta.arg.from,
          ...normalizeTaskRecordResponse(payload),
        }
      })
      .addCase(get2026YearMiddleUnionContestTaskRecord.rejected, (state, action) => {
        state.taskRecord.loading = false
        state.taskRecord.error = action.error.message || '获取闯关记录失败'
      })
      .addCase(get2026YearUnionMiddleContestTaskList.pending, (state) => {
        state.task.loading = true
        state.task.error = null
      })
      .addCase(get2026YearUnionMiddleContestTaskList.fulfilled, (state, action) => {
        state.task.loading = false
        const payload = action.payload ?? ({} as MiddleContestTaskListResponse)
        Object.assign(state.task, normalizeTaskListResponse(payload), {
          done: !state.task.done,
        })
      })
      .addCase(get2026YearUnionMiddleContestTaskList.rejected, (state, action) => {
        state.task.loading = false
        state.task.error = action.error.message || '获取闯关任务列表失败'
      })
  },
})

export default NewYearUnionGame2026Slice.reducer
export const NewYearUnionGame2026Actions = NewYearUnionGame2026Slice.actions
export const useNewYearUnionGame2026State = () =>
  useSelector((state: RootState) => state.NewYearUnionGame2026)
